import React, { useState, useEffect, useCallback } from 'react'
import apiClient, { API_ENDPOINTS } from '../../config/api'
import Swal from 'sweetalert2'
import jsPDF from 'jspdf'
import 'jspdf-autotable'
import {
  isSmallShopMode,
  locationQueryParams,
  getLocationIdFromStock
} from '../../utils/smallShopMode'

const ManageStocks = () => {
  const smallShopMode = isSmallShopMode()
  const locationLabel = smallShopMode ? 'Branch' : 'Warehouse'
  const [stocks, setStocks] = useState([])
  const [filteredStocks, setFilteredStocks] = useState([])
  const [warehouses, setWarehouses] = useState([])
  const [branches, setBranches] = useState([])
  const [mainWarehouse, setMainWarehouse] = useState(null)
  const [loading, setLoading] = useState(true)
  const [selectedLocationId, setSelectedLocationId] = useState(null)
  const [stockValue, setStockValue] = useState(null)
  const [filters, setFilters] = useState({
    search: ''
  })
  const [showIncrementModal, setShowIncrementModal] = useState(false)
  const [showAdjustmentModal, setShowAdjustmentModal] = useState(false)
  const [selectedStock, setSelectedStock] = useState(null)
  const [incrementData, setIncrementData] = useState({
    quantity: '',
    reason: ''
  })
  const [adjustmentData, setAdjustmentData] = useState({
    quantity: '',
    reason: '',
    adjustment_type: 'removal'
  })
  const [showEditModal, setShowEditModal] = useState(false)
  const [editData, setEditData] = useState({
    purchase_price: '',
    selling_price: ''
  })

  const applyFilters = useCallback(() => {
    let filtered = [...stocks]

    // Search filter (product_name, product_sku)
    if (filters.search) {
      const searchLower = filters.search.toLowerCase()
      filtered = filtered.filter(stock =>
        stock.product_name?.toLowerCase().includes(searchLower) ||
        stock.product_sku?.toLowerCase().includes(searchLower)
      )
    }

    setFilteredStocks(filtered)
  }, [stocks, filters])

  useEffect(() => {
    fetchLocations()
  }, [smallShopMode])

  useEffect(() => {
    if (smallShopMode) {
      if (branches.length > 0 && !selectedLocationId) {
        setSelectedLocationId(branches[0].id)
      }
    } else if (mainWarehouse) {
      setSelectedLocationId(mainWarehouse.id)
    }
  }, [smallShopMode, mainWarehouse, branches, selectedLocationId])

  useEffect(() => {
    if (selectedLocationId) {
      fetchStockSummary(selectedLocationId)
      fetchStockValue(selectedLocationId)
    }
  }, [selectedLocationId, smallShopMode])

  useEffect(() => {
    applyFilters()
  }, [applyFilters])

  const fetchLocations = async () => {
    try {
      if (smallShopMode) {
        const response = await apiClient.get(API_ENDPOINTS.BRANCHES)
        const branchesData = response.data.results || response.data || []
        setBranches(branchesData)
        if (branchesData.length > 0) {
          setSelectedLocationId(branchesData[0].id)
        }
        return
      }

      const response = await apiClient.get(API_ENDPOINTS.WAREHOUSES)
      const warehousesData = response.data.results || response.data || []
      setWarehouses(warehousesData)

      const main = warehousesData.find(w => w.is_main === true)
      if (main) {
        setMainWarehouse(main)
      }
    } catch (error) {
      console.error(`Error fetching ${locationLabel.toLowerCase()}s:`, error)
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.response?.data?.detail || error.response?.data?.message || `Failed to fetch ${locationLabel.toLowerCase()}s. Please try again.`,
        confirmButtonColor: '#d33'
      })
    }
  }

  const fetchStockSummary = async (locationId) => {
    try {
      setLoading(true)
      const params = locationQueryParams(locationId)
      const endpoint = smallShopMode ? API_ENDPOINTS.BRANCH_STOCK : '/api/stock/entries/'

      const response = await apiClient.get(endpoint, { params })
      const stocksData = response.data || []
      setStocks(stocksData)
      setFilteredStocks(stocksData)
    } catch (error) {
      console.error('Error fetching stock summary:', error)
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.response?.data?.detail || error.response?.data?.message || 'Failed to fetch stock summary. Please try again.',
        confirmButtonColor: '#d33'
      })
      setStocks([])
      setFilteredStocks([])
    } finally {
      setLoading(false)
    }
  }

  const fetchStockValue = async (locationId) => {
    try {
      const params = locationQueryParams(locationId)
      const endpoint = smallShopMode ? API_ENDPOINTS.BRANCH_STOCK_VALUE : '/api/stock/value/'

      const response = await apiClient.get(endpoint, { params })
      setStockValue(response.data)
    } catch (error) {
      console.error('Error fetching stock value:', error)
      // Don't show error toast for stock value, just set to null
      setStockValue(null)
    }
  }

  const handleLocationChange = (locationId) => {
    setSelectedLocationId(locationId)
  }

  const showStockDetails = (stock) => {
    const detailsHtml = `
      <div style="text-align: left; padding: 10px; background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%); border-radius: 8px; margin-bottom: 10px;">
        <div style="display: flex; align-items: center; margin-bottom: 10px; padding-bottom: 10px; border-bottom: 2px solid rgba(0,0,0,0.1);">
          <div style="width: 40px; height: 40px; background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); border-radius: 50%; display: flex; align-items: center; justify-content: center; color: white; font-size: 16px; font-weight: bold; margin-right: 10px; box-shadow: 0 2px 8px rgba(0,0,0,0.2);">
            <i class="bi bi-box"></i>
          </div>
          <div>
            <h3 style="margin: 0; color: #2c3e50; font-weight: 600; font-size: 16px;">${stock.product_name || 'N/A'}</h3>
            <p style="margin: 2px 0 0 0; color: #7f8c8d; font-size: 12px;">Product ID: ${stock.product || 'N/A'}</p>
          </div>
        </div>
        
        <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin-bottom: 8px;">
          <div style="background: white; padding: 8px; border-radius: 6px; box-shadow: 0 1px 4px rgba(0,0,0,0.1);">
            <div style="color: #7f8c8d; font-size: 10px; text-transform: uppercase; letter-spacing: 0.3px; margin-bottom: 3px;">SKU</div>
            <div style="color: #2c3e50; font-weight: 500; font-size: 12px;">${stock.product_sku || 'N/A'}</div>
          </div>
          
          <div style="background: white; padding: 8px; border-radius: 6px; box-shadow: 0 1px 4px rgba(0,0,0,0.1);">
            <div style="color: #7f8c8d; font-size: 10px; text-transform: uppercase; letter-spacing: 0.3px; margin-bottom: 3px;">${locationLabel}</div>
            <div style="color: #2c3e50; font-weight: 500; font-size: 12px;">${smallShopMode ? (stock.branch_name || 'N/A') : (stock.warehouse_name || 'N/A')}</div>
          </div>
        </div>

        <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin-bottom: 8px;">
          <div style="background: white; padding: 8px; border-radius: 6px; box-shadow: 0 1px 4px rgba(0,0,0,0.1);">
            <div style="color: #7f8c8d; font-size: 10px; text-transform: uppercase; letter-spacing: 0.3px; margin-bottom: 3px;">Supplier</div>
            <div style="color: #2c3e50; font-weight: 500; font-size: 12px;">${stock.supplier_name || 'N/A'}</div>
          </div>
          
          <div style="background: white; padding: 8px; border-radius: 6px; box-shadow: 0 1px 4px rgba(0,0,0,0.1);">
            <div style="color: #7f8c8d; font-size: 10px; text-transform: uppercase; letter-spacing: 0.3px; margin-bottom: 3px;">Quantity</div>
            <div style="color: #2c3e50; font-weight: 500; font-size: 12px;">${stock.quantity }</div>
          </div>
        </div>

        <div style="background: white; padding: 8px; border-radius: 6px; box-shadow: 0 1px 4px rgba(0,0,0,0.1); margin-bottom: 8px;">
          <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin-bottom: 6px;">
            <div>
              <div style="color: #7f8c8d; font-size: 10px; text-transform: uppercase; letter-spacing: 0.3px; margin-bottom: 3px;">Purchase Price</div>
              <div style="color: #2c3e50; font-weight: 500; font-size: 12px;">$${stock.purchase_price || 0}</div>
            </div>
            <div>
              <div style="color: #7f8c8d; font-size: 10px; text-transform: uppercase; letter-spacing: 0.3px; margin-bottom: 3px;">Selling Price</div>
              <div style="color: #2c3e50; font-weight: 500; font-size: 12px;">$${stock.selling_price || 0}</div>
            </div>
            <div>
              <div style="color: #7f8c8d; font-size: 10px; text-transform: uppercase; letter-spacing: 0.3px; margin-bottom: 3px;">Total Cost</div>
              <div style="color: #2c3e50; font-weight: 500; font-size: 12px;">$${stock.total_cost || 0}</div>
            </div>
          </div>
          <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin-bottom: 6px;">
            <div>
              <div style="color: #7f8c8d; font-size: 10px; text-transform: uppercase; letter-spacing: 0.3px; margin-bottom: 3px;">Batch Number</div>
              <div style="color: #2c3e50; font-weight: 500; font-size: 12px;">${stock.batch_number || 'N/A'}</div>
            </div>
            <div>
              <div style="color: #7f8c8d; font-size: 10px; text-transform: uppercase; letter-spacing: 0.3px; margin-bottom: 3px;">Original Batch</div>
              <div style="color: #2c3e50; font-weight: 500; font-size: 12px;">${stock.original_batch_number || 'N/A'}</div>
            </div>
          </div>
          <div style="margin-bottom: 6px;">
            <div style="color: #7f8c8d; font-size: 10px; text-transform: uppercase; letter-spacing: 0.3px; margin-bottom: 3px;">Received Date</div>
            <div style="color: #2c3e50; font-weight: 500; font-size: 12px;">${stock.received_date ? new Date(stock.received_date).toLocaleString() : 'N/A'}</div>
          </div>
          <div style="margin-bottom: 6px;">
            <div style="color: #7f8c8d; font-size: 10px; text-transform: uppercase; letter-spacing: 0.3px; margin-bottom: 3px;">Reorder Level</div>
            <div style="color: #2c3e50; font-weight: 500; font-size: 12px;">${stock.reorder_level ?? 'N/A'}</div>
          </div>
          <div style="margin-bottom: 6px;">
            <div style="color: #7f8c8d; font-size: 10px; text-transform: uppercase; letter-spacing: 0.3px; margin-bottom: 3px;">Transfer Reference</div>
            <div style="color: #2c3e50; font-weight: 500; font-size: 12px;">${stock.source_transfer_reference || 'N/A'}</div>
          </div>
          <div style="margin-bottom: 6px;">
            <div style="color: #7f8c8d; font-size: 10px; text-transform: uppercase; letter-spacing: 0.3px; margin-bottom: 3px;">Notes</div>
            <div style="color: #2c3e50; font-size: 12px; white-space: pre-wrap;">${stock.notes || 'N/A'}</div>
          </div>
          <div style="margin-top: 6px; padding-top: 6px; border-top: 1px solid rgba(0,0,0,0.05); font-size: 11px; color: #7f8c8d;">
            <div>Created By: <span style="color: #2c3e50;">${stock.created_by_email || 'N/A'}</span></div>
            <div>Created At: <span style="color: #2c3e50;">${stock.created_at ? new Date(stock.created_at).toLocaleString() : 'N/A'}</span></div>
          </div>
        </div>
      </div>
    `

    Swal.fire({
      title: '<span style="color: #2c3e50; font-size: 18px; font-weight: 600;">Stock Details</span>',
      html: detailsHtml,
      icon: 'info',
      showCloseButton: true,
      confirmButtonText: 'Close',
      confirmButtonColor: '#3085d6',
      width: '600px',
      heightAuto: false,
      padding: '1rem',
      customClass: {
        popup: 'swal2-popup-custom'
      }
    })
  }

  const handleFilterChange = (e) => {
    setFilters({
      ...filters,
      [e.target.name]: e.target.value
    })
  }

  const clearFilters = () => {
    setFilters({
      search: ''
    })
  }

  const handleExportPdf = () => {
    if (filteredStocks.length === 0) {
      Swal.fire({
        icon: 'warning',
        title: 'No Data',
        text: 'There are no stocks to export.',
        confirmButtonColor: '#3085d6'
      })
      return
    }

    const locationOptions = smallShopMode ? branches : warehouses
    const selectedLocation = locationOptions.find((loc) => loc.id === selectedLocationId)
    const locationName = selectedLocation
      ? selectedLocation.name
      : (smallShopMode ? 'All Branches' : 'All Warehouses')

    const doc = new jsPDF('landscape', 'mm', 'a4')
    doc.setFontSize(16)
    doc.text('Stock Management Report', 14, 16)
    doc.setFontSize(12)
    doc.text(`${locationLabel}: ${locationName}`, 14, 24)
    doc.text(`Generated: ${new Date().toLocaleString()}`, 14, 30)
    
    if (stockValue) {
      doc.setFontSize(10)
      doc.text(`Total Quantity: ${stockValue.total_quantity || 0} | Total Value: $${(stockValue.total_value || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} | Avg Price: $${(stockValue.average_purchase_price || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, 14, 36)
    }

    doc.autoTable({
      startY: stockValue ? 42 : 36,
      head: [['Product Name', 'SKU', 'Description', 'Purchase Price', 'Selling Price', 'Quantity', 'Total Cost']],
      body: filteredStocks.map(stock => [
        stock.product_name || 'N/A',
        stock.product_sku || 'N/A',
        stock.product_description || 'N/A',
        `$${stock.purchase_price || 0}`,
        `$${stock.selling_price || 0}`,
        stock.quantity || 0,
        `$${stock.total_cost || 0}`
      ]),
      styles: { fontSize: 9 },
      headStyles: { fillColor: [102, 126, 234] }
    })

    const fileName = `manage-stocks-${locationName.replace(/\s+/g, '-').toLowerCase()}-${new Date().toISOString().split('T')[0]}.pdf`
    doc.save(fileName)
  }

  const handleIncrementClick = (stock) => {
    setSelectedStock(stock)
    setIncrementData({
      quantity: '',
      reason: ''
    })
    setShowIncrementModal(true)
  }

  const handleAdjustmentClick = (stock) => {
    setSelectedStock(stock)
    setAdjustmentData({
      quantity: '',
      reason: '',
      adjustment_type: 'removal'
    })
    setShowAdjustmentModal(true)
  }

  const handleEditClick = (stock) => {
    setSelectedStock(stock)
    setEditData({
      purchase_price: stock.purchase_price ?? '',
      selling_price: stock.selling_price ?? ''
    })
    setShowEditModal(true)
  }

  const handleIncrementSubmit = async () => {
    if (!incrementData.quantity || parseFloat(incrementData.quantity) <= 0) {
      Swal.fire({
        icon: 'error',
        title: 'Validation Error',
        text: 'Please enter a valid quantity greater than 0.'
      })
      return
    }

    try {
      const payload = {
        stock_entry_id: selectedStock.id,
        quantity: parseFloat(incrementData.quantity),
        reason: incrementData.reason || ''
      }

      await apiClient.post('/api/stock/entries/increment/', payload)

      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: 'Stock incremented successfully!',
        confirmButtonColor: '#3085d6'
      })

      setShowIncrementModal(false)
      setIncrementData({ quantity: '', reason: '' })
      setSelectedStock(null)

      // Refresh stock data
      if (selectedLocationId) {
        fetchStockSummary(selectedLocationId)
        fetchStockValue(selectedLocationId)
      }
    } catch (error) {
      console.error('Error incrementing stock:', error)
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.response?.data?.detail || error.response?.data?.message || 'Failed to increment stock. Please try again.',
        confirmButtonColor: '#d33'
      })
    }
  }

  const handleAdjustmentSubmit = async () => {
    if (!adjustmentData.quantity || parseFloat(adjustmentData.quantity) <= 0) {
      Swal.fire({
        icon: 'error',
        title: 'Validation Error',
        text: 'Please enter a valid quantity greater than 0.'
      })
      return
    }

    if (parseFloat(adjustmentData.quantity) > parseFloat(selectedStock.quantity)) {
      Swal.fire({
        icon: 'error',
        title: 'Validation Error',
        text: 'Adjustment quantity cannot exceed available stock quantity.'
      })
      return
    }

    try {
      const locationId = getLocationIdFromStock(selectedStock)
      const payload = {
        product_id: selectedStock.product,
        quantity: parseFloat(adjustmentData.quantity),
        reason: adjustmentData.reason || '',
        adjustment_type: adjustmentData.adjustment_type
      }
      payload[smallShopMode ? 'branch_id' : 'warehouse_id'] = locationId

      await apiClient.post('/api/stock/adjustments/', payload)

      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: 'Stock adjustment made successfully!',
        confirmButtonColor: '#3085d6'
      })

      setShowAdjustmentModal(false)
      setAdjustmentData({ quantity: '', reason: '', adjustment_type: 'removal' })
      setSelectedStock(null)

      // Refresh stock data
      if (selectedLocationId) {
        fetchStockSummary(selectedLocationId)
        fetchStockValue(selectedLocationId)
      }
    } catch (error) {
      console.error('Error making stock adjustment:', error)
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.response?.data?.detail || error.response?.data?.message || 'Failed to make stock adjustment. Please try again.',
        confirmButtonColor: '#d33'
      })
    }
  }

  const handleEditSubmit = async () => {
    const hasPurchase = editData.purchase_price !== '' && editData.purchase_price !== null
    const hasSelling = editData.selling_price !== '' && editData.selling_price !== null

    if (!hasPurchase && !hasSelling) {
      Swal.fire({
        icon: 'error',
        title: 'Validation Error',
        text: 'Enter a purchase price or selling price to update.'
      })
      return
    }

    if (
      (hasPurchase && parseFloat(editData.purchase_price) < 0) ||
      (hasSelling && parseFloat(editData.selling_price) < 0)
    ) {
      Swal.fire({
        icon: 'error',
        title: 'Validation Error',
        text: 'Prices cannot be negative.'
      })
      return
    }

    const payload = {}
    if (hasPurchase) {
      payload.purchase_price = parseFloat(editData.purchase_price)
    }
    if (hasSelling) {
      payload.selling_price = parseFloat(editData.selling_price)
    }

    try {
      await apiClient.put(`/api/stock/entries/${selectedStock.id}/`, payload)

      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: 'Stock prices updated successfully!',
        confirmButtonColor: '#3085d6'
      })

      setShowEditModal(false)
      setEditData({ purchase_price: '', selling_price: '' })
      setSelectedStock(null)

      if (selectedLocationId) {
        fetchStockSummary(selectedLocationId)
        fetchStockValue(selectedLocationId)
      }
    } catch (error) {
      console.error('Error updating stock prices:', error)
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.response?.data?.detail || error.response?.data?.message || 'Failed to update stock prices. Please try again.',
        confirmButtonColor: '#d33'
      })
    }
  }

  return (
    <>
      <div className="pagetitle">
        <h1>Manage Stocks</h1>
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">Home</li>
            <li className="breadcrumb-item">Products</li>
            <li className="breadcrumb-item active">Manage Stocks</li>
          </ol>
        </nav>
      </div>

      <section className="section">
        <div className="row">
          <div className="col-lg-12">
            <div className="card">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center flex-wrap mb-3" style={{ 
                  paddingBottom: '15px',
                  borderBottom: '2px solid #e9ecef'
                }}>
                  <div className="d-flex align-items-center gap-4 flex-wrap">
                    <h5 className="card-title mb-0">
                      <i className="bi bi-boxes me-2"></i>
                      Manage Stocks
                    </h5>
                    
                    {/* Stock Value Summary Badges */}
                    {stockValue && (
                      <div className="d-flex align-items-center gap-3 flex-wrap">
                        <div className="d-flex align-items-center" style={{
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          padding: '6px 14px',
                          borderRadius: '20px',
                          color: 'white',
                          fontSize: '13px',
                          fontWeight: '600',
                          boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)'
                        }}>
                          <i className="bi bi-box-seam me-2"></i>
                          <span>Quantity: <strong>{stockValue.total_quantity || 0}</strong></span>
                        </div>
                        
                        <div className="d-flex align-items-center" style={{
                          background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                          padding: '6px 14px',
                          borderRadius: '20px',
                          color: 'white',
                          fontSize: '13px',
                          fontWeight: '600',
                          boxShadow: '0 2px 8px rgba(17, 153, 142, 0.3)'
                        }}>
                          <i className="bi bi-currency-dollar me-2"></i>
                          <span>Value: <strong>${(stockValue.total_value || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong></span>
                        </div>
                        
                        <div className="d-flex align-items-center" style={{
                          background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                          padding: '6px 14px',
                          borderRadius: '20px',
                          color: 'white',
                          fontSize: '13px',
                          fontWeight: '600',
                          boxShadow: '0 2px 8px rgba(245, 87, 108, 0.3)'
                        }}>
                          <i className="bi bi-graph-up me-2"></i>
                          <span>Avg Price: <strong>${(stockValue.average_purchase_price || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong></span>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <div className="d-flex gap-2">
                    <button 
                      className="btn btn-sm btn-outline-danger"
                      onClick={handleExportPdf}
                      disabled={loading || filteredStocks.length === 0}
                      style={{
                        borderRadius: '6px',
                        padding: '6px 14px',
                        fontSize: '13px',
                        fontWeight: '500'
                      }}
                      title="Export to PDF"
                    >
                      <i className="bi bi-file-earmark-pdf me-1"></i>
                      PDF
                    </button>
                    <button 
                      className="btn btn-sm btn-outline-primary"
                      onClick={() => {
                        if (selectedLocationId) {
                          fetchStockSummary(selectedLocationId)
                          fetchStockValue(selectedLocationId)
                        }
                      }}
                      disabled={loading}
                      style={{
                        borderRadius: '6px',
                        padding: '6px 14px',
                        fontSize: '13px',
                        fontWeight: '500'
                      }}
                    >
                      <i className="bi bi-arrow-clockwise me-1"></i>
                      Refresh
                    </button>
                  </div>
                </div>
                <p className="text-muted">View and manage stock levels by {locationLabel.toLowerCase()}.</p>

                {/* Location Selection */}
                <div className="card mb-3">
                  <div className="card-body">
                    <h6 className="card-title mb-3">
                      <i className="bi bi-building me-2"></i>
                      Select {locationLabel}
                    </h6>
                    <div className="row g-3">
                      <div className="col-md-12">
                        <label htmlFor="location" className="form-label">{locationLabel}</label>
                        <select
                          className="form-select"
                          id="location"
                          value={selectedLocationId || ''}
                          onChange={(e) => handleLocationChange(parseInt(e.target.value))}
                        >
                          {(smallShopMode ? branches : warehouses).map((location) => (
                            <option key={location.id} value={location.id}>
                              {location.name}{!smallShopMode && location.is_main ? ' (Main)' : ''}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Filters Section */}
                <div className="card mb-3">
                  <div className="card-body">
                   
                    <div className="row g-3">
                      <div className="col-md-10">
                        <label htmlFor="search" className="form-label">Search</label>
                        <input
                          type="text"
                          className="form-control"
                          id="search"
                          name="search"
                          placeholder="Product Name or SKU"
                          value={filters.search}
                          onChange={handleFilterChange}
                        />
                      </div>
                      <div className="col-md-2 d-flex align-items-end">
                        <button
                          className="btn btn-outline-secondary w-100"
                          onClick={clearFilters}
                          title="Clear filters"
                        >
                          <i className="bi bi-x-lg me-1"></i>
                          Clear
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Stocks Table */}
                {loading ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-3 text-muted">Loading stocks...</p>
                  </div>
                ) : filteredStocks.length === 0 ? (
                  <div className="text-center py-5">
                    <i className="bi bi-inbox" style={{ fontSize: '3rem', color: '#ccc' }}></i>
                    <p className="mt-3 text-muted">No stocks found</p>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-hover">
                      <thead className="table-light">
                        <tr>
                          <th>Product Name</th>
                          <th>SKU</th>
                          <th>Description</th>
                          <th>Purchase Price</th>
                          <th>Selling Price</th>
                          <th> Quantity</th>
                          <th>Total Cost</th>
                          
                          <th style={{ width: '80px', textAlign: 'center' }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredStocks.map((stock, index) => (
                          <tr key={`${stock.product_id}-${index}`}>
                            <td>
                              <div className="d-flex align-items-center">
                                <div className="avatar-sm bg-primary text-white rounded-circle d-flex align-items-center justify-content-center me-2" style={{ width: '32px', height: '32px', fontSize: '14px' }}>
                                  <i className="bi bi-box"></i>
                                </div>
                                <div className="fw-semibold">{stock.product_name || 'N/A'}</div>
                              </div>
                            </td>
                            <td>
                              <span className="badge bg-secondary">{stock.product_sku || 'N/A'}</span>
                            </td>
                            <td>
                              <span className="text-muted">{stock.product_description || 'N/A'}</span>
                            </td>
                            <td>
                              <span className="text-muted">${stock.purchase_price || 0}</span>
                            </td>
                            <td>
                              <span className="text-muted">${stock.selling_price || 0}</span>
                            </td>
                            <td>
                              <span className="fw-semibold">{stock.quantity || 0}</span>
                            </td>
                            <td>
                              <span className="fw-semibold text-success">${stock.total_cost || 0}</span>
                            </td>
                            
                            <td style={{ textAlign: 'center' }}>
                              <div className="btn-group" role="group">
                                <button
                                  className="btn btn-sm btn-outline-primary"
                                  onClick={() => showStockDetails(stock)}
                                  title="View Details"
                                >
                                  <i className="bi bi-eye"></i>
                                </button>
                                <button
                                  className="btn btn-sm btn-outline-success"
                                  onClick={() => handleIncrementClick(stock)}
                                  title="Increment Stock"
                                >
                                  <i className="bi bi-plus-circle"></i>
                                </button>
                                <button
                                  className="btn btn-sm btn-outline-warning"
                                  onClick={() => handleAdjustmentClick(stock)}
                                  title="Adjust Stock"
                                >
                                  <i className="bi bi-pencil-square"></i>
                                </button>
                                <button
                                  className="btn btn-sm btn-outline-info"
                                  onClick={() => handleEditClick(stock)}
                                  title="Edit Prices"
                                >
                                  <i className="bi bi-pencil"></i>
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Results Count */}
                {!loading && (
                  <div className="mt-3 text-muted">
                    <small>
                      Showing <strong>{filteredStocks.length}</strong> of <strong>{stocks.length}</strong> products
                    </small>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Increment Stock Modal */}
      {showIncrementModal && selectedStock && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }} tabIndex="-1">
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="bi bi-plus-circle me-2"></i>
                  Increment Stock
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowIncrementModal(false)
                    setSelectedStock(null)
                    setIncrementData({ quantity: '', reason: '' })
                  }}
                ></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Product</label>
                  <input
                    type="text"
                    className="form-control"
                    value={selectedStock.product_name || 'N/A'}
                    disabled
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Current Quantity</label>
                  <input
                    type="text"
                    className="form-control"
                    value={selectedStock.quantity || 0}
                    disabled
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Quantity to Add <span className="text-danger">*</span></label>
                  <input
                    type="number"
                    className="form-control"
                    min="0.01"
                    step="0.01"
                    value={incrementData.quantity}
                    onChange={(e) => setIncrementData({ ...incrementData, quantity: e.target.value })}
                    placeholder="Enter quantity"
                    required
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Reason (Optional)</label>
                  <textarea
                    className="form-control"
                    rows="3"
                    value={incrementData.reason}
                    onChange={(e) => setIncrementData({ ...incrementData, reason: e.target.value })}
                    placeholder="Enter reason for increment"
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowIncrementModal(false)
                    setSelectedStock(null)
                    setIncrementData({ quantity: '', reason: '' })
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-success"
                  onClick={handleIncrementSubmit}
                >
                  <i className="bi bi-check-circle me-1"></i>
                  Increment Stock
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Stock Adjustment Modal */}
      {showAdjustmentModal && selectedStock && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }} tabIndex="-1">
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="bi bi-pencil-square me-2"></i>
                  Stock Adjustment
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowAdjustmentModal(false)
                    setSelectedStock(null)
                    setAdjustmentData({ quantity: '', reason: '', adjustment_type: 'removal' })
                  }}
                ></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Product</label>
                  <input
                    type="text"
                    className="form-control"
                    value={selectedStock.product_name || 'N/A'}
                    disabled
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Current Quantity</label>
                  <input
                    type="text"
                    className="form-control"
                    value={selectedStock.quantity || 0}
                    disabled
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Adjustment Type <span className="text-danger">*</span></label>
                  <select
                    className="form-select"
                    value={adjustmentData.adjustment_type}
                    onChange={(e) => setAdjustmentData({ ...adjustmentData, adjustment_type: e.target.value })}
                    required
                  >
                    <option value="removal">Removal</option>
                    <option value="damaged">Damaged</option>
                  </select>
                </div>
                <div className="mb-3">
                  <label className="form-label">Quantity to Remove <span className="text-danger">*</span></label>
                  <input
                    type="number"
                    className="form-control"
                    min="0.01"
                    step="0.01"
                    max={selectedStock.quantity || 0}
                    value={adjustmentData.quantity}
                    onChange={(e) => setAdjustmentData({ ...adjustmentData, quantity: e.target.value })}
                    placeholder="Enter quantity"
                    required
                  />
                  <small className="form-text text-muted">
                    Maximum: {selectedStock.quantity || 0}
                  </small>
                </div>
                <div className="mb-3">
                  <label className="form-label">Reason (Optional)</label>
                  <textarea
                    className="form-control"
                    rows="3"
                    value={adjustmentData.reason}
                    onChange={(e) => setAdjustmentData({ ...adjustmentData, reason: e.target.value })}
                    placeholder="Enter reason for adjustment"
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowAdjustmentModal(false)
                    setSelectedStock(null)
                    setAdjustmentData({ quantity: '', reason: '', adjustment_type: 'removal' })
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-warning"
                  onClick={handleAdjustmentSubmit}
                >
                  <i className="bi bi-check-circle me-1"></i>
                  Make Adjustment
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Prices Modal */}
      {showEditModal && selectedStock && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }} tabIndex="-1">
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="bi bi-pencil me-2"></i>
                  Edit Stock Prices
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowEditModal(false)
                    setSelectedStock(null)
                    setEditData({ purchase_price: '', selling_price: '' })
                  }}
                ></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Product</label>
                  <input
                    type="text"
                    className="form-control"
                    value={selectedStock.product_name || 'N/A'}
                    disabled
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Purchase Price</label>
                  <input
                    type="number"
                    className="form-control"
                    min="0"
                    step="0.01"
                    value={editData.purchase_price}
                    onChange={(e) => setEditData({ ...editData, purchase_price: e.target.value })}
                    placeholder="Enter purchase price"
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Selling Price</label>
                  <input
                    type="number"
                    className="form-control"
                    min="0"
                    step="0.01"
                    value={editData.selling_price}
                    onChange={(e) => setEditData({ ...editData, selling_price: e.target.value })}
                    placeholder="Enter selling price"
                  />
                </div>
                <small className="text-muted">
                  Only prices are updated. Leave a field blank to keep its current value.
                </small>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowEditModal(false)
                    setSelectedStock(null)
                    setEditData({ purchase_price: '', selling_price: '' })
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-info"
                  onClick={handleEditSubmit}
                >
                  <i className="bi bi-check-circle me-1"></i>
                  Update Prices
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default ManageStocks

